<script setup lang="ts">
import { computed, getCurrentInstance, useTemplateRef } from 'vue';
import { BTooltip } from 'bootstrap-vue-next';
import { resourceMetaMessages } from '../../messages';

export interface Resource {
  created_by: string;
  created_at: string;
  updated_by?: string;
  updated_at?: string;
}

const props = withDefaults(
  defineProps<{
    item: Resource;
    noTooltip?: boolean;
  }>(),
  {
    noTooltip: false,
  },
);

const instance = getCurrentInstance();
const proxy = instance?.proxy as any;
const $app = computed(() => proxy?.$root?.$app);
const $filters = computed(
  () => instance?.appContext.config.globalProperties.$filters,
);

const containerRef = useTemplateRef<HTMLElement>('container');

const hasTooltip = computed(() => !props.noTooltip);

const tooltipText = computed(() => {
  if (hasTooltip.value && $filters.value?.datetime) {
    return $filters.value.datetime(dateToDisplay.value);
  }
  return '';
});

const createdBy = computed(() => {
  return (
    props.item.created_by &&
    $app.value?.users?.getUserDisplayName(props.item.created_by)
  );
});

const createdAt = computed(() => props.item.created_at);

const updatedBy = computed(() => {
  return (
    props.item.updated_by &&
    $app.value?.users?.getUserDisplayName(props.item.updated_by)
  );
});

const updatedAt = computed(() => props.item.updated_at);

const textToDisplay = computed(() => {
  return props.item.updated_at
    ? resourceMetaMessages.lastModified
    : resourceMetaMessages.created;
});

const dateToDisplay = computed(() => {
  return props.item.updated_at || props.item.created_at;
});

const userToDisplay = computed(() => {
  return props.item.updated_by || props.item.created_by;
});

function formatDatetime(value: any, opts?: any) {
  return $filters.value?.datetime?.(value, opts) ?? value;
}

function formatUsername(value: any) {
  return $filters.value?.username?.(value) ?? value;
}
</script>

<template>
  <div ref="container" class="itp-resource-meta">
    <div class="itp-resource-meta__row">
      <span class="text-muted text-end">{{ textToDisplay }}:</span>
      <ItpIcon fa="clock" :font-scale="0.8" class="text-muted"></ItpIcon>

      <div class="d-flex flex-wrap">
        <span :title="tooltipText">
          {{ formatDatetime(dateToDisplay, { distanceToNow: true }) }}
        </span>
        <span class="ms-1">({{ formatUsername(userToDisplay) }})</span>
      </div>
    </div>

    <BTooltip
      v-if="hasTooltip"
      :target="containerRef"
      :delay="300"
      variant="light"
    >
      <div>
        <span class="text-muted">{{ resourceMetaMessages.created }}:</span>
        <span>{{ formatDatetime(createdAt) }}</span>
        <span class="ms-1">({{ createdBy }})</span>
      </div>

      <div v-if="updatedAt">
        <span class="text-muted">{{ resourceMetaMessages.lastModified }}:</span>
        <span>{{ formatDatetime(updatedAt) }}</span>
        <span class="ms-1">({{ updatedBy }})</span>
      </div>
    </BTooltip>
  </div>
</template>
